// What? A React component for creating and editing documents based on document types.
// Why? To provide a structured interface for document generation with configuration and content management.
// How? The component's logic flow:
// 1. Initialize: Load document or document type information
// 2. Configure: Use ConfigPanel to set title, inputs, and source documents
// 3. Generate: Trigger AI-powered content generation
// 4. View/Edit: Display and modify generated sections
// 5. Export: Download the final document

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Card, Grid, IconButton, Tooltip, Typography } from "@mui/joy";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useAuthFetch } from "../../../../utils/useAuthFetch";

import { Document } from "../../types/documentTypes";
import ConfigPanel from "../../components/documentGeneration/ConfigPanel";
import GenerationView from "../../components/documentGeneration/GenerationView";
import CitationTooltip from "../../components/documentGeneration/CitationTooltip";
import { useDocuments } from "../../hooks/useDocuments";
import { useSourceVerification } from "../../hooks/useSourceVerification";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useFeatureFlag } from "../../../../providers/FeatureFlagProvider";
import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import PageLoading from "../../../../components/PageLoading";
import PageError from "../../../../components/PageError";
import { useErrorMessageProvider } from "../../../../components/ErrorMessageProvider";
import { useTutorialTarget, useTutorialHint } from "../../../../components/Tutorial";
import {
  applyTerminalGenerationCompletion,
  getGenerationLifecycleKey,
  replaceSectionsAfterSourceDeletion,
} from "./generationLifecycle";

const GenerateDocument: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const workspace_id = workspace?.id || "1";
  const { registerError } = useErrorMessageProvider();
  const { documentTypeId, documentId } = useParams<{
    documentTypeId: string;
    documentId: string;
  }>();
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [currentDocumentTypeId, setCurrentDocumentTypeId] = useState<
    number | null
  >(null);
  const [currentDocumentTypeVersion, setCurrentDocumentTypeVersion] = useState<
    number | null
  >(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRegeneratingSection, setIsRegeneratingSection] = useState(false);
  // Reported by ConfigPanel while a source upload/deletion or a document save
  // is in flight; blocks single-section regeneration until it clears.
  const [regenerateBlockedReason, setRegenerateBlockedReason] = useState("");
  const generationLifecycleKey = getGenerationLifecycleKey(currentDocument);

  const sourceVerificationEnabled = useFeatureFlag("meridian_rag_sources");
  const hasSourceDocuments =
    (currentDocument?.SourceDocumentCount ??
      currentDocument?.sourceDocuments?.length ??
      0) > 0;

  const {
    generationState,
    startGeneration,
    getDocumentSections,
    exportDocument,
    resumeExportJob,
    downloadExport,
    downloadFileToBrowser,
    regenerateSection,
    updateDocument,
    deleteSourceDocument: rawDeleteSourceDocument,
    createDocument,
    getDocument,
    updateGenerationState,
    updateSection,
    getSectionPrompt,
    preloadSourcePdfs,
  } = useDocuments();

  const handleTerminalGenerationComplete = useCallback(
    (completedDocumentId: number) => {
      setCurrentDocument((prev) =>
        applyTerminalGenerationCompletion(prev, completedDocumentId),
      );
    },
    [],
  );

  // Callback to update section content when reverification completes (new data-ref spans)
  const handleSectionContentUpdate = useCallback(
    (sectionId: number, content: string) => {
      if (generationState.currentSection?.section_id === sectionId) {
        updateGenerationState({
          currentSection: { ...generationState.currentSection, content },
        });
      } else {
        const idx = generationState.completedSections.findIndex(
          (s) => s.section_id === sectionId,
        );
        if (idx !== -1) {
          const updated = [...generationState.completedSections];
          updated[idx] = { ...updated[idx], content };
          updateGenerationState({ completedSections: updated });
        }
      }
    },
    [
      generationState.currentSection,
      generationState.completedSections,
      updateGenerationState,
    ],
  );

  const sourceVerification = useSourceVerification(
    documentId ? Number(documentId) : undefined,
    workspace_id,
    handleSectionContentUpdate,
  );

  const updateSourceDocumentCount = useCallback((count: number) => {
    setCurrentDocument((prev) =>
      prev
        ? {
            ...prev,
            SourceDocumentCount: count,
          }
        : prev,
    );
  }, []);

  const rawFetch = useAuthFetch("raw");
  const handleExportCitations = useCallback(async () => {
    if (!documentId) return;
    try {
      const response = await rawFetch(
        `/api/workspace/meridian/documents/${documentId}/citations/export?workspace_id=${workspace_id}`,
        "export citation report",
      );
      const blob = await (response as Response).blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = "Citation Verification Report.docx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export citation report:", err);
    }
  }, [documentId, workspace_id, rawFetch]);

  // Persistently highlight the expanded ref's span in the editor
  useEffect(() => {
    // Remove previous highlight
    document.querySelectorAll(".citation-active-ref").forEach((el) => {
      el.classList.remove("citation-active-ref");
    });

    const expandedRef = sourceVerification.expandedRef;
    if (!expandedRef) return;

    const sectionEl = document.querySelector(
      `[data-section-id="${expandedRef.sectionId}"]`,
    );
    if (!sectionEl) return;

    const refSpan = sectionEl.querySelector(
      `[data-ref="${expandedRef.refId}"]`,
    );
    if (refSpan) {
      refSpan.classList.add("citation-active-ref");
    }

    return () => {
      document.querySelectorAll(".citation-active-ref").forEach((el) => {
        el.classList.remove("citation-active-ref");
      });
    };
  }, [sourceVerification.expandedRef]);

  // Load document information when component mounts
  useEffect(() => {
    const loadDocumentInfo = async () => {
      if (documentId) {
        // Editing existing document - fetch document details
        setLoadingDocument(true);
        setError(null);
        try {
          const doc = await getDocument(Number(documentId), workspace_id);
          setCurrentDocument(doc);
          setCurrentDocumentTypeId(doc.DocumentTypeID);
          setCurrentDocumentTypeVersion(doc.DocumentType.version);
          // Pre-cache source PDFs on the server so the viewer opens instantly
          preloadSourcePdfs(Number(documentId), workspace_id);
        } catch (error) {
          console.error("Error loading document:", error);
          setError("Failed to load document. Please try again.");
        } finally {
          setLoadingDocument(false);
        }
      } else if (documentTypeId) {
        // Creating new document - use the provided document type ID
        setCurrentDocumentTypeId(Number(documentTypeId));
      }
    };

    loadDocumentInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount with stable dependencies
  }, []);

  useEffect(() => {
    if (!documentId) {
      return;
    }

    void resumeExportJob(Number(documentId), workspace_id);
  }, [documentId, resumeExportJob, workspace_id]);

  // On entering the page with an existing document, check if it already has generated sections.
  // Wait for currentDocument to be loaded so we can check its Status.
  // If generation is still in progress (Status="Started"), auto-connect via WebSocket.
  const hasAutoConnected = useRef(false);

  useEffect(() => {
    const loadExistingSectionsForDocument = async () => {
      if (!documentId || !currentDocument) return;

      // Layer 1 guard: never reload-and-overwrite while a generation is
      // actively streaming. This effect is keyed on currentDocument, and the
      // generate flow updates currentDocument (via getDocument) right before
      // starting generation -- which re-fires this effect. Its async section
      // fetch then resolves ~seconds later and would clobber the freshly
      // cleared/streaming state with stale DB sections (and mark the doc
      // complete from a stale Status).
      if (generationState.isGenerating) {
        return;
      }

      const generationFinished = currentDocument.Status !== "Started";

      try {
        const sections = await getDocumentSections(
          Number(documentId),
          workspace_id,
        );
        if (sections && sections.length > 0) {
          const completedSections = sections.map((section) => ({
            section_id: section.section_id,
            title: section.title,
            content: section.content,
            prompt: section.prompt || "",
            level: section.level,
            status: "complete" as const,
            order_index: section.order_index,
            modified_date: section.modified_date,
            section_type: section.section_type,
            thinking_events: section.thinking_events,
          }));

          // Layer 2 guard: re-check live state at write time. A generation may
          // have started while the fetch above was in flight (Layer 1 reads a
          // closure snapshot). Reading prev here is authoritative -- never
          // overwrite an in-flight stream with this stale DB snapshot.
          updateGenerationState((prev) => {
            if (prev.isGenerating) {
              return {};
            }
            return {
              completedSections,
              isComplete: generationFinished,
              isStreamComplete: generationFinished,
              existingSectionIds: new Set(sections.map((s) => s.section_id)),
            };
          });
        }

        // Auto-reconnect to generation if it's still running on the server.
        // Only reconnect when sections exist -- a brand-new document has
        // Status="Started" (the DB default) but hasn't actually begun
        // generating, so reconnecting would kick off an unwanted generation.
        if (
          !generationFinished &&
          currentDocument.GenerationActive === true &&
          !hasAutoConnected.current &&
          sections &&
          sections.length > 0
        ) {
          hasAutoConnected.current = true;
          startGeneration(
            Number(documentId),
            workspace_id,
            true,
            handleTerminalGenerationComplete,
          );
        }
      } catch (e) {
        console.error("Error checking existing sections:", e);
      }
    };

    loadExistingSectionsForDocument();
    // Reconcile only when document identity or generation status changes.
    // Source-count/file metadata updates replace currentDocument too, but must
    // not downgrade a completed generation using a stale Status value.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle key intentionally excludes source metadata
  }, [generationLifecycleKey]);

  const configPanelRef = useTutorialTarget("meridian-config-panel");
  useTutorialHint(
    {
      id: "meridian-generate-first-time",
      targetId: "meridian-config-panel",
      title: "Configure your document",
      body: "Fill in the document title, complete any required input fields, and upload your source documents. When ready, click Generate to create your document.",
      placement: "right",
    },
    !loadingDocument && !documentId,
  );

  // Ref for the generation content container, used by CitationTooltip
  const generationContainerRef = useRef<HTMLDivElement>(null);

  // Track which sections have already been processed for source verification
  const processedSectionIds = useRef<Set<number>>(new Set());

  // Clear processedSectionIds when a new generation starts
  useEffect(() => {
    if (generationState.isGenerating) {
      processedSectionIds.current.clear();
    }
  }, [generationState.isGenerating]);

  // Extract citations from WebSocket section updates and pass to source verification
  useEffect(() => {
    if (!sourceVerificationEnabled) return;
    const allSections = [
      ...generationState.completedSections,
      ...(generationState.currentSection
        ? [generationState.currentSection]
        : []),
    ];
    for (const section of allSections) {
      // If a section was already processed but arrives with new citations (regeneration),
      // clear it from the set and re-process
      if (
        section.citations &&
        section.citations.length > 0 &&
        processedSectionIds.current.has(section.section_id)
      ) {
        // Skip if already processed -- regenerated sections are handled by the
        // isGenerating reset above
        continue;
      }
      if (
        section.status === "validating" &&
        !processedSectionIds.current.has(section.section_id)
      ) {
        sourceVerification.handleValidatingSection(section.section_id);
      }
      if (
        ((section.citations && section.citations.length > 0) ||
          section.validation_error) &&
        !processedSectionIds.current.has(section.section_id)
      ) {
        sourceVerification.clearSectionCitations?.(section.section_id);
        sourceVerification.handleCitationsReceived(
          section.section_id,
          section.citations || [],
          section.validation_error,
        );
        processedSectionIds.current.add(section.section_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally tracking section changes
  }, [
    generationState.completedSections,
    generationState.currentSection,
    sourceVerificationEnabled,
  ]);

  // Surface generation errors via the global error provider.
  // GenerationView is unmounted when isGenerating=false and no sections exist,
  // so its own error effect never fires. We register the error here instead.
  const lastRegisteredError = useRef<string | null>(null);
  useEffect(() => {
    if (
      generationState.error &&
      generationState.error !== lastRegisteredError.current
    ) {
      registerError(`Generation error: ${generationState.error}`);
      lastRegisteredError.current = generationState.error;
    } else if (!generationState.error) {
      lastRegisteredError.current = null;
    }
  }, [generationState.error, registerError]);

  const handleDeleteSourceDocument = async (sourceDocumentId: number) => {
    try {
      await rawDeleteSourceDocument(sourceDocumentId, workspace_id);
      setCurrentDocument((prev) =>
        prev
          ? {
              ...prev,
              sourceDocuments: prev.sourceDocuments?.filter(
                (doc) => doc.id !== sourceDocumentId,
              ),
              SourceDocumentCount: Math.max(
                (prev.SourceDocumentCount ??
                  prev.sourceDocuments?.length ??
                  1) - 1,
                0,
              ),
            }
          : prev,
      );
      // Refresh document sections after deletion
      if (documentId) {
        const sections = await getDocumentSections(
          Number(documentId),
          workspace_id,
        );
        updateGenerationState((prev) =>
          replaceSectionsAfterSourceDeletion(prev, sections),
        );
      }
    } catch (error) {
      console.error("Error deleting source document:", error);
      throw error; // Propagate error to parent component for handling
    }
  };

  // Section loading is handled by GenerationView component to avoid race conditions
  // useEffect removed to prevent duplicate section loading

  const handleGenerate = useCallback(
    (continueGeneration: boolean) => {
      if (documentId) {
        startGeneration(
          Number(documentId),
          workspace_id,
          continueGeneration,
          handleTerminalGenerationComplete,
        );
      }
    },
    [
      documentId,
      workspace_id,
      startGeneration,
      handleTerminalGenerationComplete,
    ],
  );

  // Show loading state while document is being loaded
  if (loadingDocument) {
    return <PageLoading>Loading Document...</PageLoading>;
  }

  // Show error if there's an error loading the document
  if (error) {
    return <PageError>{error}</PageError>;
  }

  // Show error if neither documentId nor documentTypeId is available
  if (!documentId && !currentDocumentTypeId) {
    return <PageError>No document or document type specified.</PageError>;
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">
          {documentId ? "Edit Document" : "Create Document"}
        </Typography>
      </PageHeader>
      <Grid container spacing={2}>
        {/* ConfigPanel - hidden, but kept mounted, when the source panel is
            open. Unmounting it abandons in-flight source uploads, deletions
            and saves: none of them are cancelled, and FileUploader only
            reports completion from inside the component, so the regeneration
            blocker below could never clear once the panel went away. */}
        <Grid
          xs={12}
          md={5}
          sx={{
            display: sourceVerification.isPanelOpen ? "none" : "block",
          }}
        >
            <Box ref={configPanelRef}>
            <ConfigPanel
              documentTypeId={currentDocumentTypeId || Number(documentTypeId)}
              documentTypeVersion={currentDocumentTypeVersion!}
              documentId={documentId ? Number(documentId) : undefined}
              documentStatus={currentDocument?.Status}
              isGenerationActive={
                currentDocument?.GenerationActive === true
              }
              hasExistingSections={
                generationState.completedSections?.length > 0
              }
              isGenerating={generationState.isGenerating}
              isRegeneratingSection={isRegeneratingSection}
              generationState={generationState}
              onGenerate={handleGenerate}
              updateDocument={updateDocument}
              deleteSourceDocument={handleDeleteSourceDocument}
              createDocument={createDocument}
              getDocument={getDocument}
              onDocumentUpdated={setCurrentDocument}
              onSourceDocumentCountChanged={updateSourceDocumentCount}
              onSectionRegenerationBlockChange={setRegenerateBlockedReason}
            />
            </Box>
        </Grid>

        {/* MainContent - expands when source panel takes over config panel space */}
        <Grid xs={12} md={sourceVerification.isPanelOpen ? 12 : 7}>
          {documentId &&
            (generationState.isGenerating ||
              generationState.currentSection ||
              (generationState.completedSections?.length ?? 0) > 0) && (
              <Box sx={{ position: "relative" }}>
                {sourceVerificationEnabled && generationState.isComplete && (
                  <Box
                    sx={{
                      position: "sticky",
                      top: 16,
                      zIndex: 10,
                      height: 0,
                      display: "flex",
                      justifyContent: "flex-end",
                      marginRight: "-20px",
                    }}
                  >
                    {sourceVerification.isPanelOpen &&
                      sourceVerification.citationsLoaded && (
                        <Tooltip
                          title="Export verification report"
                          placement="left"
                          size="sm"
                        >
                          <IconButton
                            variant="outlined"
                            color="neutral"
                            onClick={handleExportCitations}
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              boxShadow: "sm",
                              backgroundColor: "background.surface",
                              mr: 0.5,
                              "&:hover": { backgroundColor: "neutral.100" },
                            }}
                          >
                            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    <Tooltip
                      title={
                        sourceVerification.isPanelOpen
                          ? "Hide source verification"
                          : "Verify sources"
                      }
                      placement="left"
                    >
                      <IconButton
                        variant={
                          sourceVerification.isPanelOpen ? "solid" : "outlined"
                        }
                        color={
                          sourceVerification.isPanelOpen ? "primary" : "neutral"
                        }
                        onClick={sourceVerification.togglePanel}
                        disabled={generationState.isGenerating}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          boxShadow: "sm",
                          backgroundColor: sourceVerification.isPanelOpen
                            ? "primary.500"
                            : "background.surface",
                          "&:hover": {
                            backgroundColor: sourceVerification.isPanelOpen
                              ? "primary.600"
                              : "neutral.100",
                          },
                        }}
                      >
                        <FactCheckIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Card
                  ref={generationContainerRef}
                  // overflow:visible keeps the sticky outline rail inside
                  // GenerationView anchored to the page scroll; a clipping
                  // ancestor would trap it. Same reason as the Card in
                  // DocumentTypeManager.
                  sx={{ backgroundColor: "#ffffff", overflow: "visible" }}
                >
                  <GenerationView
                    documentId={Number(documentId)}
                    templateId={
                      currentDocument?.DocumentType?.templateId || undefined
                    }
                    documentStatus={currentDocument?.Status}
                    isGenerating={generationState.isGenerating}
                    setIsGenerating={(generating: boolean) =>
                      updateGenerationState({ isGenerating: generating })
                    }
                    generationState={generationState}
                    startGeneration={handleGenerate}
                    exportDocument={exportDocument}
                    downloadExport={downloadExport}
                    downloadFileToBrowser={downloadFileToBrowser}
                    regenerateSection={regenerateSection}
                    onRegenerationActivityChange={setIsRegeneratingSection}
                    regenerateBlockedReason={regenerateBlockedReason}
                    getDocumentSections={getDocumentSections}
                    updateGenerationState={updateGenerationState}
                    updateSection={updateSection}
                    getSectionPrompt={getSectionPrompt}
                    hasSourceDocuments={hasSourceDocuments}
                    sourceVerificationEnabled={sourceVerificationEnabled}
                    isPanelOpen={sourceVerification.isPanelOpen}
                    onTogglePanel={sourceVerification.togglePanel}
                    citations={sourceVerification.citations}
                    validationStatus={sourceVerification.validationStatus}
                    validationErrors={sourceVerification.validationErrors}
                    expandedRef={sourceVerification.expandedRef}
                    onExpandRef={sourceVerification.expandRef}
                    onUpdateSectionHeight={
                      sourceVerification.updateSectionHeight
                    }
                    onClearSectionCitations={
                      sourceVerification.clearSectionCitations
                    }
                    onValidatingSection={
                      sourceVerification.handleValidatingSection
                    }
                    citationsLoaded={sourceVerification.citationsLoaded}
                  />
                </Card>
              </Box>
            )}
        </Grid>
      </Grid>
      {sourceVerificationEnabled && (
        <CitationTooltip
          citations={sourceVerification.citations}
          isPanelOpen={sourceVerification.isPanelOpen}
          containerRef={generationContainerRef}
        />
      )}
    </Page>
  );
};

export default GenerateDocument;
